import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const HomeIndustriesSection = () => {
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const swiperRef = useRef(null);

  const industries = [
    {
      title: "Construction",
      description: "Dozers, loaders, among other earthmoving equipment.",
    },
    {
      title: "Mining",
      description: "Excavators, drills, trucks & mining attachments.",
    },
    {
      title: "Oil & Gas",
      description: "From pumping & mixing trucks to skids & trailers.",
    },
    {
      title: "Quarry & Aggregate",
      description: "Off-highway dump trucks & heavy-duty front-shovels.",
    }
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 1) {
          return 0;
        }
        return prev + 0.025;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isMobile]);

  const handleSlideChange = () => {
    setProgress(0);
  };

  return (
    <section className="py-16 bg-neutral-50">
      <style>{`
        /* Custom pagination bullet styles */
        .swiper-pagination-bullet {
          width: 6px !important;
          height: 6px !important;
          background: rgba(26, 26, 26, 0.25) !important;
          border-radius: 50% !important;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
          margin: 0 4px !important;
          opacity: 1 !important;
          position: relative !important;
          overflow: hidden !important;
          cursor: pointer !important;
          border: none !important;
          outline: none !important;
          transform: scale(1) !important;
        }

        .swiper-pagination-bullet-active {
          width: 28px !important;
          height: 6px !important;
          border-radius: 12px !important;
          background: #151310 !important;
          transform: scale(1) !important;
          transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
        }

        .swiper-pagination-bullet:hover:not(.swiper-pagination-bullet-active) {
          background: #151310 !important;
          transform: scale(1.15) !important;
        }

        .swiper-pagination-bullet-active::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: ${progress * 100}%;
          background: #201C18 !important;
          border-radius: inherit;
          transition: width 0.1s ease-out !important;
          z-index: 1;
        }

        .swiper-pagination-bullet-active::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.3) 50%, 
            transparent 100%
          );
          border-radius: inherit;
          animation: smoothShimmer 2s ease-in-out infinite;
          z-index: 2;
        }

        @keyframes smoothShimmer {
          0% {
            left: -100%;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            left: 100%;
            opacity: 0;
          }
        }

        .swiper-pagination-bullet:focus {
          box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.3) !important;
          outline: none !important;
        }

        @media (max-width: 640px) {
          .swiper-pagination-bullet {
            width: 6px !important;
            height: 6px !important;
            margin: 0 6px !important;
          }
          
          .swiper-pagination-bullet-active {
            width: 36px !important;
            height: 6px !important;
            border-radius: 18px !important;
          }
        }

        .swiper-pagination-bullet,
        .swiper-pagination-bullet-active,
        .swiper-pagination-bullet::before,
        .swiper-pagination-bullet::after {
          will-change: transform, width, background-color, opacity !important;
          backface-visibility: hidden !important;
        }

        .swiper-pagination-bullet:not(.swiper-pagination-bullet-active)::before {
          width: 0% !important;
          transition: width 0.3s ease-out !important;
        }

        .swiper-pagination-bullet,
        .swiper-pagination-bullet-active {
          transition-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
        }

        .gradient-text {
          background: linear-gradient(to right, #FFD200, #F7971E);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Custom pagination positioning */
        .mobile-text-slider .swiper-pagination {
          position: relative !important;
          bottom: 0 !important;
          margin-top: 16px !important;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-1 sm:space-y-10">
            {/* Header */}
            <div className="hidden sm:block space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-0.5 bg-gradient-to-r from-[#FFD200] to-[#F7971E]"></div>
                <span className="text-base font-bold tracking-wider gradient-text uppercase">
                  INDUSTRIES WE SERVE
                </span>
              </div>

              <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 leading-tight">
                Built for Construction, Mining & Beyond
              </h2>
            </div>

            {/* Desktop - Industries List */}
            <div className="hidden sm:block space-y-6 lg:space-y-8">
              {industries.map((industry, index) => (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-start space-y-2 sm:space-y-0 sm:space-x-6 lg:space-x-8"
                >
                  <div className="sm:w-32 lg:w-40 flex-shrink-0">
                    <h3 className="text-lg lg:text-xl font-semibold text-gray-900">
                      {industry.title}
                    </h3>
                  </div>

                  <div className="flex-1 sm:border-l-2 sm:border-black sm:pl-6">
                    <p className="text-gray-500 text-base font-medium lg:text-lg leading-relaxed">
                      {industry.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile - Text Slider with Swiper */}
            <div className="sm:hidden mobile-text-slider">
              <Swiper
                ref={swiperRef}
                modules={[Pagination, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                pagination={{
                  clickable: true,
                }}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                loop={true}
                speed={600}
                onSlideChange={handleSlideChange}
                className="w-full"
              >
                {industries.map((industry, index) => (
                  <SwiperSlide key={index}>
                    <div className="space-y-3 pb-2">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {industry.title}
                      </h3>
                      <p className="text-gray-500 text-base font-medium leading-relaxed">
                        {industry.description}
                      </p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>

          {/* Right Column - Single Image */}
          <div className="order-first lg:order-last">
            {/* Mobile Header */}
            <div className="block sm:hidden py-4">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-0.5 bg-gradient-to-r from-[#FFD200] to-[#F7971E]"></div>
                <span className="text-base font-bold tracking-wider gradient-text uppercase">
                  INDUSTRIES WE SERVE
                </span>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 leading-tight my-3">
                Built for Construction, Mining & Beyond
              </h2>
            </div>

            {/* Single Static Image */}
            <div className="relative w-full h-64 sm:h-80 lg:h-96 xl:h-[500px] rounded-sm overflow-hidden">
              <img
                src="/images/homePage/equipment.webp"
                alt="Heavy equipment working in industrial setting"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeIndustriesSection;